import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouter } from 'next/router';

type ForcedSearchParams = Record<string, string | null>;

type ForcedSearchParamsContext = {
  forcedSearchParams: ForcedSearchParams;
  mergeForcedSearchParams: (next: ForcedSearchParams) => void;
};

const ForcedSearchParamsContext = createContext<ForcedSearchParamsContext>({
  forcedSearchParams: {},
  mergeForcedSearchParams: () => {},
});

export const ForcedSearchParamsProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [forcedSearchParams, setForcedSearchParams] =
    useState<ForcedSearchParams>({});
  const router = useRouter();

  const mergeForcedSearchParams = useCallback((next: ForcedSearchParams) => {
    setForcedSearchParams((prev) => ({
      ...prev,
      ...next,
    }));
  }, []);

  useEffect(() => {
    setForcedSearchParams({});
  }, [router.query]);

  return (
    <ForcedSearchParamsContext.Provider
      value={{ forcedSearchParams, mergeForcedSearchParams }}
    >
      {children}
    </ForcedSearchParamsContext.Provider>
  );
};

/*
 * If we manually update the URL query params without doing NextJS navigation,
 * we need to keep track of the query param change we made so that other links
 * (via LinkWrapper) can update their URLs accordingly.
 */
export const useForcedSearchParams = (): ForcedSearchParamsContext =>
  useContext(ForcedSearchParamsContext);

export default ForcedSearchParamsContext;
